// ---- W3-F import landed cost ----
export type BoeStatus = 'DRAFT' | 'POSTED' | 'REVERSED';
export type AllocationMethod = 'VALUE' | 'QTY' | 'WEIGHT' | 'MANUAL';
export type ChargeKind = 'CD' | 'RD' | 'SD' | 'VAT' | 'AT' | 'AIT' | 'CNF' | 'PORT' | 'FREIGHT' | 'INSURANCE' | 'OTHER';
export type ChargeTreatment = 'CAPITALISE' | 'INPUT_VAT' | 'ADVANCE_INCOME_TAX';
export type CreditSource = 'CUSTOMS' | 'BANK' | 'SUPPLIER_BILL';

export const CHARGE_KINDS: ChargeKind[] = ['CD', 'RD', 'SD', 'VAT', 'AT', 'AIT', 'CNF', 'PORT', 'FREIGHT', 'INSURANCE', 'OTHER'];
export const CREDIT_SOURCES: CreditSource[] = ['CUSTOMS', 'BANK', 'SUPPLIER_BILL'];

/** Mirrors the backend ChargeKind.defaultTreatment() so the form can pre-fill the treatment. */
export const defaultTreatment = (kind: ChargeKind): ChargeTreatment => {
  if (kind === 'VAT' || kind === 'AT') return 'INPUT_VAT';
  if (kind === 'AIT') return 'ADVANCE_INCOME_TAX';
  return 'CAPITALISE';
};

export interface BoeLineInput {
  goodsReceiptLineId: string;
  weight?: number | null;
  manualAmount?: number | null;
}

export interface BoeChargeInput {
  kind: ChargeKind;
  treatment?: ChargeTreatment | null;
  creditSource: CreditSource;
  amount: number;
  description?: string;
  supplierBillId?: string | null;
  bankGlAccountId?: string | null;
  reference?: string;
}

export interface BillOfEntryRequest {
  institutionId: string;
  boeNumber: string;
  boeDate: string;
  customsHouse?: string;
  assessableValue?: number | null;
  currencyCode?: string;
  exchangeRate?: number | null;
  allocationMethod: AllocationMethod;
  backToBackLcId?: string | null;
  b2bAcceptanceId?: string | null;
  notes?: string;
  lines: BoeLineInput[];
  charges: BoeChargeInput[];
}

export interface BoeLine {
  id: string; goodsReceiptId: string; grnNumber?: string; goodsReceiptLineId: string; itemId: string;
  itemName?: string; itemCode?: string; storeId?: string; segmentId?: string; receivedQty: number; lineValue: number;
  weight?: number; manualAmount?: number; allocatedAmount?: number; onHandAmount?: number; consumedAmount?: number;
}

export interface BoeCharge {
  id: string; seq: number; kind: ChargeKind; treatment: ChargeTreatment; creditSource: CreditSource; amount: number;
  description?: string; supplierBillId?: string; supplierBillNumber?: string; bankGlAccountId?: string; reference?: string;
}

export interface CustomsPayment {
  id: string; institutionId?: string; billOfEntryId?: string; boeNumber?: string; paymentDate: string; amount: number;
  bankGlAccountId: string; challanNo: string; challanDate: string; journalEntryId?: string;
  reversalJournalEntryId?: string; reversedAt?: string; chargeIds?: string[];
}

export interface BillOfEntry {
  id: string; institutionId: string; boeNumber: string; boeDate: string; customsHouse?: string;
  assessableValue?: number; currencyCode?: string; exchangeRate?: number; assessableValueBase?: number;
  allocationMethod: AllocationMethod; status: BoeStatus; backToBackLcId?: string; b2bAcceptanceId?: string; notes?: string;
  capitalisedTotal: number; inputTaxTotal: number; aitTotal: number; chargesTotal: number;
  journalEntryId?: string; reversalJournalEntryId?: string; postedAt?: string; reversedAt?: string;
  customsPayable: number; customsPaid: number; customsOutstanding: number;
  lines: BoeLine[]; charges: BoeCharge[]; payments: CustomsPayment[];
}

export interface BillOfEntryListItem {
  id: string; boeNumber: string; boeDate: string; customsHouse?: string; status: BoeStatus;
  chargesTotal: number; capitalisedTotal: number; inputTaxTotal: number; aitTotal: number; grnCount: number;
  journalEntryId?: string;
}

export interface AllocationPreview {
  capitalisePool: number; inputTaxTotal: number; aitTotal: number; customsCredit: number; bankCredit: number;
  clearingCredit: number; manualDifference: number; warnings: string[];
  lines: { goodsReceiptLineId: string; weight?: number; allocatedAmount: number }[];
}

export interface CustomsPaymentRequest {
  billOfEntryId: string; paymentDate: string; amount: number; bankGlAccountId: string; challanNo: string;
  challanDate: string; chargeIds?: string[];
}

export interface CnfBillOption {
  billId: string; billNumber: string; supplierInvoiceRef?: string; billDate: string; totalAmount: number;
  clearingDebit: number; alreadyCharged: number; available: number;
}

export interface GrnLineOption {
  goodsReceiptId: string; grnNumber?: string; goodsReceiptLineId: string; itemId: string; itemName?: string;
  itemCode?: string; receivedQty: number; lineValue: number;
}

export interface GrnLineOptionPage {
  content: GrnLineOption[]; totalElements: number; totalPages: number; number: number; size: number;
}

export interface BillOfEntryPage {
  content: BillOfEntryListItem[]; totalElements: number; totalPages: number; number: number; size: number;
}

export const TREATMENT_LABELS: Record<ChargeTreatment, string> = {
  CAPITALISE: 'Capitalise into stock cost',
  INPUT_VAT: 'Input VAT',
  ADVANCE_INCOME_TAX: 'Advance income tax (AIT)',
};

export const CREDIT_SOURCE_LABELS: Record<CreditSource, string> = {
  CUSTOMS: 'Customs (payable)',
  BANK: 'Bank / cash',
  SUPPLIER_BILL: 'C&F agent bill',
};

/** Charge kinds that can be derived from a percentage, and the base each one applies to. */
export const PCT_BASE: Partial<Record<ChargeKind, 'AV' | 'AV_PLUS_DUTIES'>> = {
  CD: 'AV', RD: 'AV', SD: 'AV', AIT: 'AV', VAT: 'AV_PLUS_DUTIES', AT: 'AV_PLUS_DUTIES',
};
